import type { ScmpUser } from "@product/scmp-account-sdk";
import { useAtomValue, useSetAtom } from "jotai";
import { useRouter } from "next/router";
import { useCallback, useEffect } from "react";

import { getGoogleAnalyticsClientId } from "~/components/tracking/google-analytics/apis";
import { accountAtom } from "~/lib/account/atoms";
import { isNoExternalScripts } from "~/lib/utils";

import { fetchRealtimeProfile, fetchUserAudienceId } from "./apis";
import { onePlusXAtom } from "./atoms";
import type { UserType } from "./tracks";
import {
  sendOnePlusXEvent,
  sendRouteChangedOnePlusXEvent,
  sendVisitedOnePlusXEvent,
} from "./tracks";

export function useTrackPageLoad() {
  const { user } = useAtomValue(accountAtom);

  useEffect(() => {
    const run = async () => {
      const gaClientId = await getGoogleAnalyticsClientId();

      if (!gaClientId) return;

      sendOnePlusXEvent({
        ...trackGoogleAnalyticsId(gaClientId),
        ...trackScmpUserId(user),
        ...trackUserType(user),
        type: "pageLoad",
      });
      const visitUrl = encodeURI(window.location.href);
      sendVisitedOnePlusXEvent(visitUrl);
    };

    function trackGoogleAnalyticsId(gaClientId: string | undefined) {
      if (!gaClientId) return;

      return {
        cd32: gaClientId,
      };
    }

    function trackScmpUserId(user: ScmpUser | null) {
      if (!user?.uuid) return;
      return {
        cd34: user.uuid,
      };
    }

    function trackUserType(user: ScmpUser | null) {
      let userType: UserType = "anonymous";
      if (user) userType = "Authenticated";
      // TODO: determine user type from subscription information from rosetta subscription status

      return {
        userType,
      };
    }

    void run();
  }, [user]);
}

export function useTrackRouteChange() {
  const { events: routerEvents } = useRouter();

  useEffect(() => {
    const handler = () => {
      const visitUrl = encodeURI(window.location.href);
      sendRouteChangedOnePlusXEvent(visitUrl);
      sendVisitedOnePlusXEvent(visitUrl);
    };
    routerEvents.on("routeChangeComplete", handler);

    return () => {
      routerEvents.off("routeChangeComplete", handler);
    };
  }, [routerEvents]);
}

export const useOnePlusXPageTargeting = () => {
  const value = useAtomValue(onePlusXAtom);
  useEffect(() => {
    googletag.cmd.push(() => {
      if (value.profile) {
        googletag.pubads().setTargeting("ope-audience", value.profile.audienceSegmentIds);
        googletag.pubads().setTargeting("ope-topic", value.profile.topicIds);
      }
      if (value.audienceId) {
        googletag.pubads().setTargeting("1plusX_uid0", value.audienceId.part0);
        googletag.pubads().setTargeting("1plusX_uid1", value.audienceId.part1);
      }
    });
  }, [value]);
};

export const useOnePlusXSetup = () => {
  const { events: routerEvents } = useRouter();

  const setOnePlusX = useSetAtom(onePlusXAtom);
  const fetchOnePlusXValue = useCallback(() => {
    if (isNoExternalScripts()) return;
    // Workaround router.events.on typing requirement
    const run = async () => {
      const [realTimeProfileResult, userAudienceIdResult] = await Promise.allSettled([
        fetchRealtimeProfile(),
        fetchUserAudienceId(),
      ]);
      setOnePlusX(v => {
        let { audienceId, profile } = v;
        if (realTimeProfileResult.status === "fulfilled") {
          profile = realTimeProfileResult.value;
        }
        if (userAudienceIdResult.status === "fulfilled") {
          audienceId = userAudienceIdResult.value;
        }
        return { audienceId, profile };
      });
    };

    void run();
  }, [setOnePlusX]);

  useEffect(() => {
    fetchOnePlusXValue();
  }, [fetchOnePlusXValue]);
  useEffect(() => {
    routerEvents.on("routeChangeComplete", fetchOnePlusXValue);
    return () => {
      routerEvents.off("routeChangeComplete", fetchOnePlusXValue);
    };
  }, [routerEvents, fetchOnePlusXValue]);
};
